import { UseFormReturn } from 'react-hook-form';
import { useLanguage } from '@/hooks/useLanguage';
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CreditCard, DollarSign, Building, CheckCircle, AlertTriangle } from 'lucide-react';
import { useState, useEffect } from 'react';

interface PaymentStepProps {
  form: UseFormReturn<any>;
  onZelleValidationChange?: (isValid: boolean) => void;
}

const PaymentStep = ({ form, onZelleValidationChange }: PaymentStepProps) => {
  const { t, language } = useLanguage();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const paymentMethod = form.watch('paymentMethod');
  const zelleConfirmation = form.watch('zelleConfirmation');

  // Track Zelle validation state (requires BOTH confirmation AND receipt)
  const [zelleError, setZelleError] = useState(false);
  const [receiptError, setReceiptError] = useState(false);

  useEffect(() => {
    if (paymentMethod === 'Zelle') {
      const hasConfirmation = !!zelleConfirmation && zelleConfirmation.trim().length > 0;
      const hasReceipt = !!uploadedFile;
      const isValid = hasConfirmation && hasReceipt;
      
      setZelleError(!hasConfirmation);
      setReceiptError(!hasReceipt);
      onZelleValidationChange?.(isValid);
    } else {
      setZelleError(false);
      setReceiptError(false);
      onZelleValidationChange?.(true);
    }
  }, [paymentMethod, zelleConfirmation, uploadedFile, onZelleValidationChange]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.size <= 20 * 1024 * 1024) { // 20MB limit
      setUploadedFile(file);
      form.setValue('paymentEvidenceFile', file);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold mb-2">
          {language === 'es' ? 'Método de Pago' : 'Payment Method'}
        </h3>
        <p className="text-muted-foreground">
          {language === 'es' 
            ? 'Tarifa de consulta: $150 USD (no reembolsable)'
            : 'Consultation Fee: $150 USD (non-refundable)'}
        </p>
      </div>

      <div className="bg-destructive/20 border-2 border-destructive rounded-lg p-6" role="alert" aria-live="polite">
        <div className="flex items-start gap-3">
          <DollarSign className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-destructive text-lg mb-2">
              {language === 'es' ? '⚠️ Política de Pago' : '⚠️ Payment Policy'}
            </p>
            <p className="text-destructive font-semibold">
              {language === 'es'
                ? 'La tarifa de consulta de $150 no es reembolsable. Este pago asegura su cita.'
                : 'The $150 consultation fee is non-refundable. This payment secures your appointment.'}
            </p>
          </div>
        </div>
      </div>

      <FormField
        control={form.control}
        name="paymentMethod"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {language === 'es' ? 'Seleccione Método de Pago' : 'Select Payment Method'} *
            </FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="grid grid-cols-1 gap-4"
              >
                {/* Stripe - Credit/Debit Card */}
                <Card 
                  className={`cursor-pointer transition-all ${
                    field.value === 'Stripe' ? 'border-primary bg-primary/5' : ''
                  }`}
                  onClick={() => field.onChange('Stripe')}
                >
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="Stripe" id="stripe" />
                      <CreditCard className="h-5 w-5 text-primary" />
                      <CardTitle className="text-base">
                        {language === 'es' ? 'Tarjeta de Crédito/Débito' : 'Credit/Debit Card'}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>
                      {language === 'es'
                        ? 'Pago seguro con tarjeta. Procesado inmediatamente por Stripe.'
                        : 'Secure card payment. Processed immediately via Stripe.'}
                    </CardDescription>
                  </CardContent>
                </Card>

                {/* Zelle */}
                <Card 
                  className={`cursor-pointer transition-all ${
                    field.value === 'Zelle' ? 'border-primary bg-primary/5' : ''
                  }`}
                  onClick={() => field.onChange('Zelle')}
                >
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="Zelle" id="zelle" />
                      <Building className="h-5 w-5 text-primary" />
                      <CardTitle className="text-base">Zelle</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>
                      {language === 'es'
                        ? 'Transferencia bancaria instantánea vía Zelle.'
                        : 'Instant bank transfer via Zelle.'}
                    </CardDescription>
                  </CardContent>
                </Card>

                {/* Check/Wire */}
                <Card 
                  className={`cursor-pointer transition-all ${
                    field.value === 'Check' || field.value === 'Wire' ? 'border-primary bg-primary/5' : ''
                  }`}
                  onClick={() => field.onChange('Check')}
                >
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="Check" id="check" />
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <CardTitle className="text-base">
                        {language === 'es' ? 'Cheque / Transferencia Bancaria' : 'Check / Wire Transfer'}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>
                      {language === 'es'
                        ? 'Pago mediante cheque o transferencia bancaria directa.'
                        : 'Payment via check or direct wire transfer.'}
                    </CardDescription>
                  </CardContent>
                </Card>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Payment Instructions */}
      {paymentMethod === 'Stripe' && (
        <Alert>
          <CreditCard className="h-4 w-4" />
          <AlertDescription>
            <p className="text-sm">
              {language === 'es'
                ? 'Será redirigido a una página segura de Stripe para completar su pago después de enviar este formulario.'
                : 'You will be redirected to a secure Stripe payment page after submitting this form.'}
            </p>
          </AlertDescription>
        </Alert>
      )}

      {paymentMethod === 'Zelle' && (
        <div className="space-y-4">
          {/* PROMINENT WARNING - Always visible when Zelle is selected and incomplete */}
          {(zelleError || receiptError) && (
            <div className="bg-destructive border-2 border-destructive rounded-lg p-6 animate-pulse" role="alert" aria-live="assertive">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-8 w-8 text-destructive-foreground flex-shrink-0" />
                <div>
                  <p className="font-bold text-destructive-foreground text-xl mb-2">
                    {language === 'es' ? '⚠️ PROCESO INCOMPLETO' : '⚠️ INCOMPLETE PROCESS'}
                  </p>
                  <p className="text-destructive-foreground font-medium mb-3">
                    {language === 'es'
                      ? 'No podrá continuar hasta completar los siguientes pasos:'
                      : 'You cannot proceed until you complete the following steps:'}
                  </p>
                  <ul className="list-disc list-inside text-destructive-foreground space-y-1">
                    <li className={zelleError ? 'font-bold' : 'line-through opacity-60'}>
                      {language === 'es' ? 'Ingresar el número de confirmación de Zelle' : 'Enter the Zelle confirmation number'}
                    </li>
                    <li className={receiptError ? 'font-bold' : 'line-through opacity-60'}>
                      {language === 'es' ? 'Subir el comprobante de pago' : 'Upload the payment receipt'}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Success message when complete */}
          {!zelleError && !receiptError && (
            <Alert className="border-green-500 bg-green-500/10">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-700 font-medium">
                {language === 'es' 
                  ? '✓ Proceso Zelle completado correctamente. Puede continuar.'
                  : '✓ Zelle process completed successfully. You may proceed.'}
              </AlertDescription>
            </Alert>
          )}

          <Card className={zelleError || receiptError ? 'border-destructive border-2' : 'border-green-500'}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                {language === 'es' ? 'Instrucciones para Zelle' : 'Zelle Instructions'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="font-semibold text-sm">
                  {language === 'es' ? 'Enviar pago a:' : 'Send payment to:'}
                </p>
                <p className="text-sm font-mono bg-muted px-2 py-1 rounded">info@danielalbertlaw.com</p>
                <p className="text-sm font-semibold mt-2">
                  {language === 'es' ? 'Monto:' : 'Amount:'} <span className="text-lg">$150.00 USD</span>
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {language === 'es'
                    ? 'Por favor incluya su nombre completo en la nota de Zelle.'
                    : 'Please include your full name in the Zelle memo.'}
                </p>
              </div>
            
              <FormField
                control={form.control}
                name="zelleConfirmation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={zelleError ? 'text-destructive font-bold' : ''}>
                      {language === 'es' ? 'Número de Confirmación de Zelle' : 'Zelle Confirmation Number'} *
                    </FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="Z123456789" 
                        className={zelleError ? 'border-destructive border-2 focus:ring-destructive' : ''}
                      />
                    </FormControl>
                    <FormDescription>
                      {language === 'es'
                        ? 'REQUERIDO: Ingrese el número de confirmación después de completar la transferencia'
                        : 'REQUIRED: Enter confirmation number after completing the transfer'}
                    </FormDescription>
                    {zelleError && (
                      <p className="text-sm text-destructive font-medium mt-1">
                        {language === 'es'
                          ? '⚠️ El número de confirmación de Zelle es obligatorio'
                          : '⚠️ Zelle confirmation number is required'}
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Upload Receipt - REQUIRED for Zelle */}
              <div className="space-y-2">
                <FormLabel className={receiptError ? 'text-destructive font-bold' : ''}>
                  {language === 'es' ? 'Subir Comprobante de Pago' : 'Upload Payment Receipt'} *
                </FormLabel>
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                  className={`cursor-pointer ${receiptError ? 'border-destructive border-2' : ''}`}
                />
                {uploadedFile ? (
                  <p className="text-sm text-green-600 font-medium">
                    ✓ {uploadedFile.name}
                  </p>
                ) : receiptError ? (
                  <p className="text-sm text-destructive font-medium">
                    {language === 'es'
                      ? '⚠️ El comprobante de pago es obligatorio'
                      : '⚠️ Payment receipt is required'}
                  </p>
                ) : null}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {(paymentMethod === 'Check' || paymentMethod === 'Wire') && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {language === 'es' ? 'Instrucciones de Pago' : 'Payment Instructions'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="font-semibold text-sm mb-2">
                {language === 'es' ? 'Cheque:' : 'Check:'}
              </p>
              <p className="text-sm">
                {language === 'es' ? 'Hacer cheque a nombre de:' : 'Make check payable to:'}<br />
                <span className="font-semibold">Daniel Albert Law Firm</span>
              </p>
              <p className="text-sm mt-2">
                {language === 'es' ? 'Enviar a:' : 'Mail to:'}<br />
                10925 Beechnut St A106<br />
                Houston, TX 77072
              </p>
            </div>

            <div className="border-t pt-3">
              <p className="font-semibold text-sm mb-2">
                {language === 'es' ? 'Transferencia Bancaria:' : 'Wire Transfer:'}
              </p>
              <p className="text-sm">
                {language === 'es'
                  ? 'Por favor contáctenos para obtener la información bancaria:'
                  : 'Please contact us for wire transfer details:'}
                <br />
                <a href="tel:8329303059" className="text-primary hover:underline">
                  (832) 930-3059
                </a>
                {' | '}
                <a href="mailto:info@danielalbertlaw.com" className="text-primary hover:underline">
                  info@danielalbertlaw.com
                </a>
              </p>
            </div>

            {/* Upload Receipt */}
            <div className="space-y-2 pt-3 border-t">
              <FormLabel>
                {language === 'es' ? 'Subir Comprobante (Opcional)' : 'Upload Receipt (Optional)'}
              </FormLabel>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileUpload}
                className="cursor-pointer"
              />
              {uploadedFile && (
                <p className="text-sm text-muted-foreground">
                  ✓ {uploadedFile.name}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PaymentStep;
